import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material/styles';
import {
  Box,
  Card,
  Link,
  Stack,
  Popover,
  Tooltip,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';

import { useAuth } from '../../../hooks/useAuth';
import Iconify from '../../../components/iconify/Iconify';
import { useConfirmModal } from '../../../hooks/useModal';
import {
  createFavorite,
  deleteFavorite,
  fetchFavoritesByTypeAndTypeId,
} from '../../../services/favorite.service';

const StyledFormImage = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  padding: '24px',
});

FormCard.propTypes = {
  form: PropTypes.object,
  removeForm: PropTypes.func,
  setSelectedForm: PropTypes.func,
  setOpenDetailedView: PropTypes.func,
  departmentsData: PropTypes.array,
};

export default function FormCard({
  form,
  removeForm,
  setSelectedForm,
  setOpenDetailedView,
  departmentsData,
}) {
  const [open, setOpen] = useState(null);
  const [favorite, setFavorite] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const { id: currentUserId, permissions } = useAuth();
  const { showToast, hideToast } = useToast();

  const canUpdate = permissions?.includes('FORM:UPDATE');
  const canDelete = permissions?.includes('FORM:DELETE');
  const showOptions = canUpdate || canDelete;

  const resolvedDepartmentName =
    departmentsData?.find((d) => d.id === form.departmentId)?.name ||
    t('ticket.department');

  // Logic to copy ID to clipboard
  const handleCopyId = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(form.id.toString());
    showToast({
      message: `${t('nav.forms')} ID ${form.id} ${
        t('successMessages.copied') || 'copied!'
      }`,
      severity: 'success',
      props: { hideToast },
    });
  };

  const handleOpenMenu = (e) => {
    setOpen(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(false);
  };

  const { showConfirmModal } = useConfirmModal(
    t('pages.formPage.confirmDelete'),
    `${t('pages.formPage.areYouSure')} ${form.title} ?`,
    () => {
      removeForm(form.id);
      setOpen(false);
    }
  );

  const checkFavorite = async ({ type, typeId }) => {
    const res = await fetchFavoritesByTypeAndTypeId({ type, typeId });
    const favorite = res.filter((fav) => fav.userId === currentUserId);
    if (favorite.length > 0) setFavorite(favorite[0]);
  };

  const handleDeletFavorite = async () => {
    genericApiToaster(
      deleteFavorite,
      favorite.id,
      '',
      t('errorMessages.errorDeletingFavorite'),
      showToast,
      hideToast,
      t,
      () => setFavorite(false)
    );
  };

  const handleAddFavorite = async () => {
    genericApiToaster(
      createFavorite,
      {
        type: 'form',
        typeId: form.id,
        userId: currentUserId,
      },
      '',
      t('errorMessages.cannotAddFavorite'),
      showToast,
      hideToast,
      t,
      setFavorite
    );
  };

  const handleFavoriteClick = () => {
    if (!isDebouncing) {
      setIsDebouncing(true);
      if (favorite) {
        handleDeletFavorite();
      } else {
        handleAddFavorite();
      }
      setTimeout(() => {
        setIsDebouncing(false);
      }, 500);
    }
  };

  useEffect(() => {
    setFavorite();
    checkFavorite({ type: 'form', typeId: form.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <>
      <Card sx={{ height: '100%', position: 'relative' }}>
        <Tooltip title={t('favorites.addToFavorites')}>
          <IconButton
            disabled={isDebouncing}
            onClick={handleFavoriteClick}
            sx={{
              position: 'absolute',
              cursor: isDebouncing ? 'default' : 'pointer',
              right: 5,
              top: 5,
              zIndex: 2,
              padding: isSmallScreen ? '2px' : '8px',
            }}
          >
            <Iconify
              width={isSmallScreen ? 20 : 25}
              color={isDebouncing ? '' : '#db0000'}
              icon={favorite ? 'carbon:favorite-filled' : 'carbon:favorite'}
            />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            pt: '100%',
            position: 'relative',
            cursor: canUpdate ? 'pointer' : 'default',
            zIndex: 1,
          }}
          onClick={() => {
            if (!isDebouncing && canUpdate) {
              setSelectedForm(form);
              setOpenDetailedView(true);
            }
          }}
        >
          <StyledFormImage alt="form" src="/assets/icons/form.png" />
        </Box>

        <Stack spacing={isSmallScreen ? 0.5 : 1} sx={{ p: 2 }}>
          <Stack justifyContent="space-between" alignItems="center" direction="row">
            <Link
              color="inherit"
              underline="hover"
              onClick={() => {
                if (canUpdate) {
                  setSelectedForm(form);
                  setOpenDetailedView(true);
                }
              }}
              sx={{
                maxWidth: '85%',
                overflow: 'hidden',
                cursor: canUpdate ? 'pointer' : 'default',
              }}
              title={form.title}
            >
              <Typography
                variant="h6"
                sx={{ fontSize: isSmallScreen ? '0.775rem' : '1rem', fontWeight: 700 }}
              >
                {form.title}
              </Typography>
            </Link>
            {showOptions && (
              <IconButton size="small" color="inherit" onClick={(e) => handleOpenMenu(e)}>
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title={t('buttons.copy') || 'Copy ID'}>
              <Stack
                direction="row"
                alignItems="center"
                onClick={handleCopyId}
                sx={{
                  cursor: 'pointer',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  px: 0.8,
                  borderRadius: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, mr: 0.5 }}>
                  #{form.id}
                </Typography>
                <Iconify icon="solar:copy-bold" width={12} />
              </Stack>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600 }}
            >
              {resolvedDepartmentName}
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: isSmallScreen ? '0.675rem' : '0.875rem',
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {form.description}
          </Typography>
        </Stack>
      </Card>
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {canUpdate && (
          <MenuItem
            onClick={() => {
              setSelectedForm(form);
              setOpenDetailedView(true);
              handleCloseMenu();
            }}
          >
            <Iconify icon="eva:edit-fill" sx={{ mr: 2, width: 20, height: 20 }} />
            {t('popover.edit')}
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={showConfirmModal} sx={{ color: 'error.main' }}>
            <Iconify icon="eva:trash-2-outline" sx={{ mr: 2, width: 20, height: 20 }} />
            {t('popover.delete')}
          </MenuItem>
        )}
      </Popover>
    </>
  );
}
